import { useState } from 'react';

import {
  Box,
  CircularProgress,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';

import { useAppSelector } from 'store';
import { canaisSelector, SelectChannelInterface } from 'store/reducers/canais';
import getIconTipoCanal from 'utils/getIconTipoCanal';

const useStyles = makeStyles(() => ({
  truncateValue: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  truncateText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  icon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
}));

interface ChannelSelectFieldProps {
  value: string;
  style?: React.CSSProperties;
  onFilter: (canal: SelectChannelInterface) => void;
  onReset: () => void;
}

export default function ChannelSelectField({
  value,
  style,
  onFilter,
  onReset,
}: ChannelSelectFieldProps) {
  const classes = useStyles();
  const { canaisSelect = [], loadingCanaisSelect } =
    useAppSelector(canaisSelector);

  const [open, setOpen] = useState(false);

  const selectedCanal = canaisSelect.find(c => c.code === value) ?? null;

  const handleChange = (
    _event: React.ChangeEvent<Record<string, unknown>>,
    canal: SelectChannelInterface | null,
  ) => {
    if (canal) {
      onFilter(canal);
    } else {
      onReset();
    }
  };

  return (
    <Autocomplete
      id="channel-select-field"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selectedCanal}
      options={canaisSelect}
      loading={loadingCanaisSelect}
      size="small"
      onChange={handleChange}
      popupIcon={<ExpandMore style={{ fontSize: 18 }} />}
      closeText="Fechar"
      openText="Abrir"
      clearText="Limpar"
      getOptionLabel={option => option?.name || ''}
      getOptionSelected={(option, value) => option.code === value.code}
      noOptionsText={
        <Typography variant="body2">Nenhum canal encontrado</Typography>
      }
      style={{ minWidth: 150, width: '100%', ...style }}
      renderOption={canal => (
        <Box className={classes.truncateValue}>
          <span className={classes.icon}>
            {getIconTipoCanal({
              tipoCanal: canal.type,
              size: 16,
              isModoHibrido: canal.isHybridMode,
            })}
          </span>
          <span className={classes.truncateText}>{canal.name}</span>
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label="Canal"
          variant="outlined"
          placeholder="Buscar canal..."
          InputProps={{
            ...params.InputProps,
            startAdornment: selectedCanal ? (
              <>
                <span className={classes.icon}>
                  {getIconTipoCanal({
                    tipoCanal: selectedCanal.type,
                    size: 16,
                    isModoHibrido: selectedCanal.isHybridMode,
                  })}
                </span>
                {params.InputProps.startAdornment}
                <Box mr={0.5} />
              </>
            ) : (
              params.InputProps.startAdornment
            ),
            endAdornment: loadingCanaisSelect ? (
              <CircularProgress size={16} />
            ) : (
              params.InputProps.endAdornment
            ),
          }}
        />
      )}
    />
  );
}
